import React, { useRef } from 'react';
import { Button, Tooltip } from '@mui/material';
import { zipDownloadUrl, ZIP_MAX_FILES, ZIP_MAX_BYTES } from '../../utils/zipDownload';

/**
 * A bulk-download button that streams the observation's files as one ZIP from the
 * server (#686) — works in every browser (no File System Access API). Rendered as
 * the fallback for browsers without the folder picker.
 *
 * A native <a download> can't surface a server 4xx, so we guard client-side:
 * disabled (with a reason pointing at Push Externally) when unauthenticated or over
 * the file OR byte cap; otherwise a real anchor that streams the zip. `fileCount`
 * and `totalBytes` should be the RAW-only totals; `maxFiles`/`maxBytes` come from
 * the server's effective `zip_limits` (fall back to the client defaults).
 */
export default function ZipDownloadButton({
  obsId,
  format = 'raw',
  fileCount = 0,
  totalBytes = 0,
  maxFiles = ZIP_MAX_FILES,
  maxBytes = ZIP_MAX_BYTES,
  label,
  icon = null,
  color,
}) {
  const url = zipDownloadUrl(obsId, format);
  const lastClick = useRef(0);
  const empty = fileCount <= 0;
  const tooMany = maxFiles > 0 && fileCount > maxFiles;
  const tooBig = maxBytes > 0 && totalBytes > maxBytes;

  if (!url || empty || tooMany || tooBig) {
    let reason;
    if (!url) reason = 'Sign in to download';
    else if (empty) reason = 'No files to download';
    else if (tooMany) reason = `Too many files for a single zip (${fileCount} > ${maxFiles}). Use "Push Externally".`;
    else reason = `Too large for a single zip (~${Math.round(totalBytes / 1e9)} GB). Use "Push Externally".`;
    return (
      <Tooltip title={reason}>
        <span>
          <Button variant="outlined" size="small" color={color} startIcon={icon} disabled>
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  // Native <a download>. On click: (1) debounce so a double-click doesn't start two
  // streams (two server-side calibration passes), and (2) re-mint the token so a page
  // left open past the access-token TTL doesn't navigate to a stale `?token=` (which
  // 401s and saves the JSON error under a .zip name).
  const onClick = (e) => {
    const now = Date.now();
    if (now - lastClick.current < 1500) {
      e.preventDefault();
      return;
    }
    lastClick.current = now;
    const fresh = zipDownloadUrl(obsId, format);
    if (fresh) e.currentTarget.href = fresh;
    else e.preventDefault(); // token vanished between render and click — don't navigate to a stale ?token=
  };

  return (
    <Button
      component="a"
      href={url}
      download
      onClick={onClick}
      variant="outlined"
      size="small"
      color={color}
      startIcon={icon}
    >
      {label}
    </Button>
  );
}
